// mssql value-slot adapter (T9, Docs/AutocompleteValueSlot-Plan.md) — turns
// the expression arm's existing column discovery into normalized
// ValueSlotColumnDescriptors for the shared policy (D10). CTE/derived
// projection goes through the SAME machinery the arm uses
// (`resolveCteColumns` / `inferSubqueryColumns`) — not reimplemented; live
// table columns reuse the arm's own lazy column cache via the same two
// primitives `emitAliasColumns` calls (`getCachedMssqlColumns` /
// `ensureMssqlColumns` from `mssqlColumnCache`), matching G27's async
// contract (`collectScopedColumns` is async here).
//
// mssql has no search_path — an unqualified relation binding resolves by
// name alone (first snapshot match), mirroring `emitAliasColumns`'s own
// relation-lookup rule: a schema-qualified binding must match that schema
// exactly, an unqualified binding takes the first name match ignoring
// schema (no default-schema substitution — `emitAliasColumns` only falls
// back to `'dbo'` when calling the column cache, not when locating the
// table).
//
// `@` local-variable discovery (`emitMssqlProceduralLocalCandidates` /
// `emitMssqlBatchLocalCandidates`) is ASYNC (worker-bridged ANTLR scope
// resolution via `getProceduralScope`), but `ValueSlotEngineAdapter.
// extraScalarSources` (T7 `valueSlot/types.ts`) is a SYNC-only contract.
// Reusing the real discovery machinery here would require either
// reimplementing a synchronous local-variable scan (duplicating discovery
// logic, which this task forbids) or blocking on the worker bridge inside a
// sync callback (impossible). Per the T9 escape hatch, `extraScalarSources`
// is left undefined here — instead the resolver's scalar-value BRANCH emits
// `@` locals itself after the policy call (same guarded async emitters the
// clause path uses; T13b escalation fix — see the branch in
// `mssqlResolver.ts`). `inserted`/`deleted` pseudo-tables are never emitted
// here regardless (not a scalar source; T9 requirement).

import type { AliasBinding, CteBinding, FkEdge, RoutineMeta } from '@/core/engines';
import type { ValueSlotInfo } from '@/features/sql-grammar/model';
import { applyPivotTransform, parsePivotClause } from '../pivot/parsePivotClause';
import { inferSubqueryColumns } from '../resolver/inferSubqueryColumns';
import { resolveCteColumns } from '../resolver/resolveCteColumns';
import { resolveLhsInScope, type ScopedOwner } from '../valueSlot/scopedResolve';
import {
  coarseTypeOfDataType,
  isBooleanishDataType,
  type ValueSlotColumnDescriptor,
  type ValueSlotEngineAdapter,
  type ValueSlotRoutineDescriptor,
  type ValueSlotScope,
  type ValueSlotVirtualColumn,
} from '../valueSlot/types';
import { formatIdentifier, unquoteIdentifier } from './mssqlIdentifiers';
import { findMssqlTable, mssqlDefaultSchemas, type MssqlDefaultSchemas } from './mssqlDefaultSchema';
import { ensureMssqlColumns, getCachedMssqlColumns } from './mssqlColumnCache';
import { noteCompletionIoWait } from '../telemetry/providerCost';
import type { OrchestratorResolverInput } from '../orchestrator/types';

type AdapterTable = {
  readonly name: string;
  readonly schema?: string;
  readonly columns: ReadonlyArray<{ readonly name: string; readonly dataType: string }>;
};

/** Everything the adapter needs that is independent of the LHS operand. */
type MssqlAdapterSources = {
  readonly text: string;
  readonly aliasBindings: readonly AliasBinding[];
  readonly cteBindings: readonly CteBinding[];
  readonly snapshotTables: ReadonlyArray<AdapterTable>;
  readonly snapshotRoutines: readonly RoutineMeta[];
  readonly snapshotForeignKeys: readonly FkEdge[];
  readonly input: OrchestratorResolverInput;
  /** C2 P1-1/P1-2 — resolver-supplied fallback for bindings whose relation
   *  is VIRTUAL: sys/INFORMATION_SCHEMA catalog views, and mssql synthetic
   *  relations (@table variables, #temp tables, OPENJSON AS alias). The
   *  resolver passes a closure over its own catalog map + scanned
   *  synthRels. Consulted only when the snapshot lookup yields nothing. */
  readonly virtualColumnsFor?: (binding: AliasBinding) => readonly ValueSlotVirtualColumn[] | undefined;
  /** Where an unqualified table name resolves (default schema, then `dbo`); `dbo` as a guess when absent. */
  readonly defaultSchemas?: MssqlDefaultSchemas;
};

/** Scalar-value mode: a slot is REQUIRED. */
export type MssqlValueSlotContext = MssqlAdapterSources & {
  readonly slot: ValueSlotInfo;
};

/** Predicate-head mode (PH-D3, P1-sol F7): NO slot — a bare boolean-expression
 *  head has no LHS operand, so the scope resolves to
 *  `{ lhsType: 'unknown', lhsBooleanish: false }` and no column is ever
 *  flagged `fkPartnerOfLhs`. Declared as `slot?: undefined` rather than by
 *  omission so the overloads stay mutually exclusive: a caller passing a slot
 *  cannot land on this signature, and an existing scalar-value caller that
 *  DROPPED its slot fails to compile instead of silently degrading to
 *  no-LHS discovery. */
export type MssqlPredicateHeadContext = MssqlAdapterSources & {
  readonly slot?: undefined;
};

/** Implementation-side union — never the public parameter type. */
type MssqlAdapterContext = MssqlValueSlotContext | MssqlPredicateHeadContext;

/** mssql "fold" — `unquoteIdentifier` + lower-case, the same rule
 *  `eqIdentifier`/the resolver's own `enrichCandidatesWithDocs` call use. */
const fold = (s: string): string => unquoteIdentifier(s).toLowerCase();

/** Relation lookup — parity with the resolver's private `emitAliasColumns`
 *  table match: a schema-qualified binding matches exactly, an unqualified
 *  one resolves as SQL Server does (`findMssqlTable`). */
function lookupRelation(
  tables: ReadonlyArray<AdapterTable>,
  binding: AliasBinding,
  defaults: MssqlDefaultSchemas = mssqlDefaultSchemas(undefined),
): AdapterTable | undefined {
  return findMssqlTable(
    tables,
    binding.schema ? fold(binding.schema) : null,
    fold(binding.relation),
    defaults,
    (a, b) => fold(a) === fold(b),
  );
}

function fkPartner(
  fks: readonly FkEdge[],
  lhs: { table: AdapterTable; column: string },
  cand: { table: AdapterTable; column: string },
): boolean {
  const match = (
    e: FkEdge,
    a: { table: AdapterTable; column: string },
    b: { table: AdapterTable; column: string },
  ) =>
    e.fromColumns.length === 1 &&
    e.fromTable.toLowerCase() === a.table.name.toLowerCase() &&
    e.fromColumns[0]!.toLowerCase() === a.column.toLowerCase() &&
    e.toTable.toLowerCase() === b.table.name.toLowerCase() &&
    e.toColumns[0]!.toLowerCase() === b.column.toLowerCase();
  return fks.some((e) => match(e, lhs, cand) || match(e, cand, lhs));
}

/** Same two cache primitives `emitAliasColumns`'s private `ensureColumnsForTable`
 *  wrapper calls — the cache/fetch machinery itself lives in
 *  `mssqlColumnCache` and is reused verbatim, not reimplemented. */
async function ensureColumnsForTable(
  connectionId: string,
  database: string,
  schema: string,
  table: string,
): Promise<ReadonlyArray<{ readonly name: string; readonly dataType: string }>> {
  const cached = getCachedMssqlColumns(connectionId, database, schema, table);
  if (cached !== undefined) return cached;
  // Cold / stale: the caller now waits on the IPC fetch (new or in flight) —
  // real I/O for the provider-cost measurement (`providerCost.ts`).
  noteCompletionIoWait();
  return ensureMssqlColumns(connectionId, database, schema, table);
}

/** Resolve the slot's LHS to (owners, coarse type, boolean-ish) — the D5
 *  all-agree rule collapses disagreeing owners to 'unknown'. */
function resolveLhs(args: MssqlAdapterContext): {
  owners: readonly ScopedOwner[];
  scope: ValueSlotScope;
} {
  // No slot (predicate-head mode) → no LHS → the neutral scope, via the same
  // path a slot with an unresolvable LHS already took.
  const lhs = args.slot?.lhs;
  if (!lhs) return { owners: [], scope: { lhsType: 'unknown', lhsBooleanish: false } };
  const owners = resolveLhsInScope({
    lhs,
    aliasBindings: args.aliasBindings,
    cteBindings: args.cteBindings,
    snapshotTables: args.snapshotTables.map((t) => ({
      name: t.name,
      schema: t.schema,
      kind: 'table' as const,
      columns: t.columns.map((c) => ({
        name: c.name, dataType: c.dataType, nullable: true, isPrimaryKey: false, ordinal: 0,
      })),
    })),
    source: args.text,
    engineId: 'mssql',
    fold,
  });
  const types = owners.map((o) =>
    coarseTypeOfDataType('dataType' in o.column ? o.column.dataType : undefined),
  );
  const allAgree = types.length > 0 && types.every((t) => t === types[0]);
  const bools = owners.map((o) =>
    isBooleanishDataType('dataType' in o.column ? o.column.dataType : undefined, 'mssql'),
  );
  return {
    owners,
    scope: {
      lhsType: allAgree ? types[0]! : 'unknown',
      lhsBooleanish: bools.length > 0 && bools.every(Boolean),
    },
  };
}

export function buildMssqlValueSlotAdapter(args: MssqlValueSlotContext): {
  adapter: ValueSlotEngineAdapter;
  scope: ValueSlotScope;
};
export function buildMssqlValueSlotAdapter(args: MssqlPredicateHeadContext): {
  adapter: ValueSlotEngineAdapter;
  scope: ValueSlotScope;
};
export function buildMssqlValueSlotAdapter(args: MssqlAdapterContext): {
  adapter: ValueSlotEngineAdapter;
  scope: ValueSlotScope;
} {
  const { owners, scope } = resolveLhs(args);
  const lhsOwner = owners.length === 1 ? owners[0] : undefined;
  const lhsTable =
    lhsOwner && (lhsOwner.binding.kind === 'table' || lhsOwner.binding.kind === 'view')
      ? lookupRelation(args.snapshotTables, lhsOwner.binding, args.defaultSchemas)
      : undefined;
  const lhsColumn = lhsOwner ? lhsOwner.column.name : undefined;

  const collectScopedColumns = async (): Promise<readonly ValueSlotColumnDescriptor[]> => {
    const out: ValueSlotColumnDescriptor[] = [];
    for (const b of args.aliasBindings) {
      const spelling = b.aliasSql ?? b.alias;
      // `spelled` (P8a-fix) — pivoted names only: a PIVOT value alias comes
      // back BRACKETED from `parsePivotClause` (`[2011]` — the engine's own
      // column spelling, which the clause path emits verbatim), and
      // `formatIdentifier` on an already-delimited token would double-bracket
      // it (`[[2011]]]`). Every pre-existing caller omits the parameter, so
      // non-pivot descriptors keep their byte-identical shape.
      const push = (
        name: string,
        dataType: string | undefined,
        table?: AdapterTable,
        spelled?: string,
      ) => {
        const bare = spelled ?? formatIdentifier(name);
        out.push({
          label: name,
          insertBare: bare,
          insertQualified: `${spelling}.${bare}`,
          owner: b,
          coarseType: coarseTypeOfDataType(dataType),
          booleanish: isBooleanishDataType(dataType, 'mssql'),
          fkPartnerOfLhs:
            table !== undefined &&
            lhsTable !== undefined &&
            lhsColumn !== undefined &&
            fkPartner(
              args.snapshotForeignKeys,
              { table: lhsTable, column: lhsColumn },
              { table, column: name },
            ),
        });
      };
      if (b.kind === 'subquery' || b.kind === 'derived') {
        const writerDeclared = b.syntheticColumns && b.syntheticColumns.length > 0;
        let names: readonly string[] = writerDeclared
          ? b.syntheticColumns!
          : b.bodyRange
            ? inferSubqueryColumns({
                source: args.text,
                bodyRange: b.bodyRange,
                engineId: 'mssql',
                snapshotTables: args.snapshotTables,
                cteBindings: args.cteBindings,
              })
            : [];
        // P8a-fix — post-PIVOT shape parity with the resolver's
        // `emitAliasColumns` pivot arm (the C1 "PIVOT ranges" gap, closed for
        // mssql here as P8c closed it for oracle, because the head flip
        // turned it from a value-slot degradation into a regression of the
        // clause path's pivot-aware behavior): a `( … ) src PIVOT (…) p`
        // binding's projection is the PIVOTED shape — the FOR +
        // aggregate-argument columns are consumed, the value aliases
        // (`[2011]`, …) are added — never the raw pre-pivot inference.
        // Mirrors the resolver exactly (no name canonicalization on mssql —
        // its clause path pushes `applyPivotTransform`'s output verbatim);
        // writer-declared COLUMNS lists bypass the transform, matching the
        // adapter's pre-existing syntheticColumns precedence.
        let pivoted = false;
        if (!writerDeclared && b.pivotRange) {
          const parsed = parsePivotClause(
            args.text.slice(b.pivotRange.start, b.pivotRange.end),
          );
          if (parsed) {
            names = applyPivotTransform(names, parsed);
            pivoted = true;
          }
        }
        for (const n of names) {
          // Pivoted names carry the engine's own spelling (`[2011]` stays
          // bracketed) — see the `spelled` note at `push`.
          push(n, undefined, undefined, pivoted ? formatIdentifier(unquoteIdentifier(n)) : undefined);
        }
        if (names.length === 0) {
          for (const v of args.virtualColumnsFor?.(b) ?? []) push(v.name, v.dataType);
        }
        continue;
      }
      if (b.kind === 'cte') {
        const cte = args.cteBindings.find((c) => fold(c.name) === fold(b.relation));
        if (!cte) continue;
        const names = resolveCteColumns({
          cte,
          source: args.text,
          engineId: 'mssql',
          snapshotTables: args.snapshotTables,
          cteBindings: args.cteBindings,
        });
        for (const n of names) push(n, undefined);
        continue;
      }
      const tbl = lookupRelation(args.snapshotTables, b, args.defaultSchemas);
      if (!tbl) {
        for (const v of args.virtualColumnsFor?.(b) ?? []) push(v.name, v.dataType);
        continue;
      }
      // In-buffer CREATE TABLE entries / test fixtures carry columns inline
      // already (mirrors `emitAliasColumns`'s same shortcut) — only pay the
      // async cache round-trip when the snapshot table's columns are empty
      // (the live bus's normal shape today, per `mssqlColumnCache.ts`).
      if (tbl.columns.length > 0) {
        for (const c of tbl.columns) push(c.name, c.dataType, tbl);
        continue;
      }
      if (!args.input.connectionId || !args.input.database) continue;
      const cols = await ensureColumnsForTable(
        args.input.connectionId,
        args.input.database,
        tbl.schema ?? 'dbo',
        tbl.name,
      );
      for (const c of cols) push(c.name, c.dataType, tbl);
    }
    return out;
  };

  const collectScalarRoutines = (): readonly ValueSlotRoutineDescriptor[] =>
    collectMssqlScalarRoutineDescriptors(args.snapshotRoutines);

  return {
    adapter: { engine: 'mssql', collectScopedColumns, collectScalarRoutines },
    scope,
  };
}

/** Scalar-routine descriptor mapping shared by this adapter's
 *  `collectScalarRoutines` closure and the resolver's T18b typed-VALUES-
 *  element call site (`emitTypedValuesElement` in `mssqlResolver.ts`) — kept
 *  in one place so the two can't drift. */
export function collectMssqlScalarRoutineDescriptors(
  snapshotRoutines: readonly RoutineMeta[],
): readonly ValueSlotRoutineDescriptor[] {
  return snapshotRoutines
    .filter((r) => r.kind !== 'procedure')
    .map((r) => ({
      label: r.name,
      insertText: `${r.name}($0)`,
      returnsCoarse: coarseTypeOfDataType(r.returns),
      returnsRaw: r.returns,
      // PH-D4 (P1-sol F6) — read only by the predicate-head policy, where a
      // boolean return promotes to T3. Harmless to the value-slot policy,
      // which ignores the field. NOTE (mssql-specific gap, see the P6a
      // report): `fetchMssqlRoutines`/`aggregateRoutineRows`
      // (mssqlSchemaIntrospection.ts) never populate `returns` for a SCALAR
      // function today — the introspection query explicitly excludes the
      // `parameter_id = 0` return-value row from `sys.parameters`, and only
      // table-valued functions get the synthetic `'table'` marker. So this
      // is correctly wired but inert in production until mssql's routine
      // introspection is extended to capture the scalar return type (Section
      // A of the spec did this for pg/mysql only).
      returnsBooleanish: isBooleanishDataType(r.returns, 'mssql'),
    }));
}
